<template>
  <div class="list-renderer">
    <div
      :style="{ height: `${pagination.itemCount * itemHeight}px` }"
    >
      <div
        class="list-renderer__wrapper"
        :style="{
          transform: `translate(0, ${previousItemCount * itemHeight}px)`,
        }"
      >
        <div
          v-for="(item, itemIndex) in itemsToRender"
          :key="visibleItemWindowIndices.firstIndex + itemIndex"
          class="list-renderer__item"
          :style="itemStyle"
        >
          <slot
            v-bind="{
              itemIndex: visibleItemWindowIndices.firstIndex + itemIndex,
              item,
            }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import debounce from 'lodash/debounce';
import { Model, Request } from '@viddsee/server-api-client';

const DEFAULT_PER_PAGE = 20;
const DEFAULT_ITEM_HEIGHT = 50;

interface VisibleItemWindowIndices {
  firstIndex: number;
  lastIndex: number;
  firstPageIndex: number;
  lastPageIndex: number;
}
type RenderItem = any;

export default Vue.extend({
  props: {
    initialItemIndex: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: Number,
      default: DEFAULT_PER_PAGE,
    },

    itemFetcher: {
      type: Function as PropType<(options: Request.Pagination.Options) => Promise<Model.Pagination.PaginatedItemsResponse<RenderItem>>>,
      required: true,
    },

    itemHeight: {
      type: Number,
      default: DEFAULT_ITEM_HEIGHT,
    },

    fetchDebounceTime: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      items: {} as Record<number, RenderItem[]>,
      isLoading: {} as Record<number, boolean | undefined>,
      pagination: {
        perPage: this.perPage,
        pageCount: 0,
        itemCount: 0,
      },

      clientHeight: 0,
      scrollTop: 0,
    };
  },

  computed: {
    visibleItemCount(): number {
      return Math.ceil(this.clientHeight / this.itemHeight);
    },
    firstVisibleIndex(): number {
      return Math.floor(this.scrollTop / this.itemHeight);
    },

    visibleItemWindowIndices(): VisibleItemWindowIndices {
      const visibleItemCount = this.visibleItemCount;
      const firstIndex = this.firstVisibleIndex;
      const lastIndex = Math.min(
        this.firstVisibleIndex + visibleItemCount,
        this.pagination.itemCount - 1,
      );
      const firstPageIndex = Math.floor(firstIndex / this.pagination.perPage);
      const lastPageIndex = Math.floor(lastIndex / this.pagination.perPage);

      return {
        firstIndex,
        lastIndex,
        firstPageIndex,
        lastPageIndex,
      };
    },

    previousItemCount(): number {
      return this.visibleItemWindowIndices.firstIndex;
    },

    itemStyle(): { height: string } {
      return ({ height: `${this.itemHeight}px` });
    },

    itemsToRender(): RenderItem[] {
      const {
        firstIndex,
        lastIndex,
      } = this.visibleItemWindowIndices;

      const itemsRenderLength = (lastIndex - firstIndex) + 1;
      const itemsToRender = [];

      for (let i = 0; i < itemsRenderLength; i++) {
        const renderIndex = i + firstIndex;
        const cachePageIndex = (renderIndex / this.pagination.perPage) | 0;
        const cachePageItemIndex = renderIndex % this.pagination.perPage;
        itemsToRender.push((this.items[cachePageIndex] && this.items[cachePageIndex][cachePageItemIndex]) || null);
      }

      return itemsToRender;
    },
  },

  watch: {
    visibleItemWindowIndices(indices): void {
      this.dispatchFetchInstruction(indices);
    },
  },

  created() {
    this.dispatchFetchInstruction = debounce(this.dispatchFetchInstruction.bind(this), this.fetchDebounceTime);
  },

  mounted() {
    this.fetchItems({
      page: (this.initialItemIndex / this.pagination.perPage) | 0,
      perPage: this.pagination.perPage,
    })
      .then(() => {
        this.$el.scrollTop = (this.initialItemIndex - 1) * this.itemHeight;

        this.handleScroll();
        this.$el.addEventListener('scroll', this.handleScroll);
      })
    ;
  },

  beforeDestroy() {
    this.$el.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    handleScroll(): void {
      this.clientHeight = this.$el.clientHeight;
      this.scrollTop = this.$el.scrollTop;
    },

    fetchItemsFromCacheOrRemote({ pageIndex }: { pageIndex: number }): void {
      if (this.items[pageIndex] || this.isLoading[pageIndex]) return;

      this.$set(this.isLoading, pageIndex, true);

      this.fetchItems({
        perPage: this.pagination.perPage,
        page: pageIndex,
      })
        .then(() => {
          this.isLoading[pageIndex] = false;
        })
        .catch((error) => {
          this.isLoading[pageIndex] = false;
          // TODO: Use a centralized error logger
          // eslint-disable-next-line no-console
          console.error(error);
        });
      ;
    },

    fetchItems({ perPage, page }: Request.Pagination.Options): Promise<Model.Pagination.PaginatedItemsResponse<RenderItem>> {
      return this.itemFetcher({ perPage, page })
        .then((response) => {
          this.$set(this.items, response.page, response.items);

          // Update pagination
          this.pagination.pageCount = response.pageCount;
          this.pagination.itemCount = response.itemCount;
          this.pagination.perPage = response.perPage;

          return response;
        })
      ;
    },

    dispatchFetchInstruction({ firstPageIndex, lastPageIndex }: VisibleItemWindowIndices): void {
      for (let neededPageIndex = firstPageIndex; neededPageIndex <= lastPageIndex; neededPageIndex += 1) {
        this.fetchItemsFromCacheOrRemote({ pageIndex: neededPageIndex });
      }
    },
  },
});
</script>

<style lang="scss">
.list-renderer {
  overflow-y: auto;
}
</style>
